import React from 'react';
import { Alert, Skeleton } from '@material-ui/lab';
import { Fade, Collapse } from '@mui/material';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import useMobileLayout from 'Hooks/useMobileLayout';
import ConflictingMessageListItem from './ConflictingMessageListItem';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(4),
    minWidth: '100%',
    backgroundColor: theme.palette.common.white,
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '1.2em',
  },
  alert: {
    marginTop: theme.spacing(2),
  },
  conflictingMessagesDesktop: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  conflictingMessagesMobile: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  alertSkeleton: {
    display: 'flex',
    flex: 1,
    margin: theme.spacing(2, 0),
  },
  skeletons: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});

const ConflictingScheduledMessages = ({
  className,
  classes,
  meetingAgendaCollection,
  refreshMeetingAgendas,
  scheduledMessagesLoading,
  outOfOfficeEnabled,
}) => {
  const eventTrackingLogEventProps = useEventTrackingLogEvent({
    nodeName: 'Out of Office Unreviewed Scheduled Meeting Agendas',
  });

  const hasConflictingMessages = meetingAgendaCollection?.resources().list().length >= 1;
  const isMobile = useMobileLayout();

  return (
    <Collapse
      in={outOfOfficeEnabled}
    >
      <div
        className={classNames(classes.root, className)}
      >
        <Typography
          className={classes.title}
          color="primary"
          align="left"
          variant="h2"
        >
          {'Scheduled Messages'}
        </Typography>
        {!scheduledMessagesLoading && (
          <div
            className={classes.alert}
            test={`${hasConflictingMessages ? 'has-conflicting' : 'no-conflicting'}-messages-alert`}
          >
            <Fade in>
              <Alert
                severity={hasConflictingMessages ? 'warning' : 'success'}
                color={hasConflictingMessages ? 'warning' : 'success'}
              >
                <Typography
                  className={classes.alertTitle}
                  variant={'h6'}
                  color={hasConflictingMessages ? 'primary' : 'secondary'}
                >
                  {hasConflictingMessages
                    ? 'The following scheduled messages cannot be sent while you\'re away. Please cancel or reschedule below.'
                    : 'You have no scheduled messages while you\'re away.'
                  }
                </Typography>
              </Alert>
            </Fade>
          </div>)
        }
        {scheduledMessagesLoading && (
          <Fade in>
            <div className={classes.alertSkeleton}>
              <Skeleton animation="wave" variant="rect" height={60} width={'100%'} />
            </div>
          </Fade>)
        }
        {!scheduledMessagesLoading && hasConflictingMessages && (
          <Fade in>
            <div
              className={classNames({
                [classes.conflictingMessagesDesktop]: !isMobile,
                [classes.conflictingMessagesMobile]: isMobile,
              })}
            >
              {meetingAgendaCollection.resources().list().map(meetingAgendaRes => {
                return (
                  <ConflictingMessageListItem
                    key={meetingAgendaRes.id()}
                    meetingAgendaResource={meetingAgendaRes}
                    refreshMeetingAgendas={refreshMeetingAgendas}
                    {...eventTrackingLogEventProps}
                  />
                );
              })}
            </div>
          </Fade>)
        }
        {scheduledMessagesLoading && (
          <div className={classNames(classes.skeletons, {
            [classes.conflictingMessagesDesktop]: !isMobile,
            [classes.conflictingMessagesMobile]: isMobile,
          })}
          >
            <Skeleton animation="wave" variant="rect" height={97} width={isMobile ? '100%' : '48%'} />
            <Skeleton animation="wave" variant="rect" height={97} width={isMobile ? '100%' : '48%'} />
          </div>)
        }
      </div>
    </Collapse>
  );
};

ConflictingScheduledMessages.defaultProps = {
  className: '',
};

ConflictingScheduledMessages.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  meetingAgendaCollection: PropTypes.array.isRequired,
  refreshMeetingAgendas: PropTypes.func.isRequired,
  scheduledMessagesLoading: PropTypes.bool.isRequired,
  outOfOfficeEnabled: PropTypes.bool.isRequired,
};

export default withStyles(styles)(ConflictingScheduledMessages);
